import { Component, Input, OnChanges, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { Measurement, StageTreeNode } from '@cosmos/shared';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { MeasurementsService } from '@cosmos/angular/api';
import { ErrorService } from '@cosmos/angular/utils';
import { MultiLineChartComponent } from '../../data/charts/multi-line-chart/multi-line-chart.component';

@Component({
  selector: 'cosmos-compare-measurements',
  imports: [
    DividerModule, ButtonModule, CardModule, MultiLineChartComponent,
    TranslatePipe, FormlyModule, ReactiveFormsModule, TreeModule
  ],
  templateUrl: './compare-measurements.component.html',
})
export class CompareMeasurementsComponent implements OnChanges {

  @Input()
  stageTree: StageTreeNode[] = [];

  compareForm = new UntypedFormGroup({});
  formFields: FormlyFieldConfig[];
  model: any = {};

  treeNodes: TreeNode[] = [];
  selectedNodes: TreeNode[] = [];

  measurementsToCompare = signal<Measurement[]>([]);
  loadingChart = signal(false);

  variable = signal<string | undefined>(undefined);

  private readonly measurementsService = inject(MeasurementsService);
  private readonly errorService = inject(ErrorService);

  constructor(
    private translateService: TranslateService
  ) { }

  ngOnChanges(): void {
    this.treeNodes = this.buildCheckboxTree(this.stageTree);
    this.selectedNodes = [];

    const variables: {value: string | null, label: string}[] = [
      {value: 'strength', label: this.translateService.instant('variables.strength')},
      {value: 'temperature', label: this.translateService.instant('variables.temperature')}
    ];

    this.formFields = [
      {
        fieldGroupClassName: 'flex gap-2',
        fieldGroup: [
          {
            className: 'grow',
            key: 'variable',
            wrappers: ['default-wrapper'],
            type: 'select',
            props: {
              required: true,
              options: variables,
              label: this.translateService.instant('compare_measurements.labels.variable')
            },
          }
        ]
      }
    ]
  }

  // Converts stage subtrees into PrimeNG checkbox-tree nodes, recursively.
  // Stage nodes are containers only; measurement leaves are what actually
  // gets compared. Tree nodes only carry a thin projection of each
  // measurement (see getDescendantTree's field-projection constraint) — no
  // time-series data — so submit() re-fetches full documents by id rather
  // than resolving them from here.
  buildCheckboxTree(stages: StageTreeNode[]): TreeNode[] {
    return stages.map((stage) => {
      const childStageNodes = this.buildCheckboxTree(stage.children);
      const measurementNodes: TreeNode[] = stage.measurements.map((m) => ({
        label: m.name,
        data: { type: 'measurement', id: m._id },
        leaf: true,
        icon: 'fa-solid fa-chart-line',
      }));
      return {
        label: stage.name,
        data: { type: 'stage', id: stage._id },
        children: [...childStageNodes, ...measurementNodes],
        icon: 'fa-solid fa-folder',
      };
    });
  }

  submit() {
    const selectedIds = this.selectedNodes
      .filter((n) => n.data.type === 'measurement')
      .map((n) => n.data.id as string);

    if (selectedIds.length === 0) {
      this.measurementsToCompare.set([]);
      return;
    }

    this.variable.set(this.model.variable);
    this.loadingChart.set(true);
    this.measurementsService.find(`?_id=${selectedIds.join(',')}`).subscribe({
      next: (res) => {
        this.measurementsToCompare.set((res.body as Measurement[]) ?? []);
        this.loadingChart.set(false);
      },
      error: (e) => {
        this.errorService.handleError(e);
        this.loadingChart.set(false);
      },
    });
  }

}
